import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, TrendingUp, Zap, Edit2, BarChart2, Loader2, Trash2, Globe, GlobeLock, Eye, PenLine } from 'lucide-react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { useConfirm } from '../hooks/useConfirm';

// ── Mini sparkline ────────────────────────────────────────────────────────────

function MiniSparkline({ data }) {
  if (!data || data.length < 2) {
    return <div className="h-12 rounded bg-surface-card/60" />;
  }
  const W = 240, H = 48;
  const values = data.map((d) => d.value);
  const minV = Math.min(...values);
  const maxV = Math.max(...values);
  const rangeV = maxV - minV || 1;
  const xScale = (i) => (i / (data.length - 1)) * W;
  const yScale = (v) => H - ((v - minV) / rangeV) * (H - 4) - 2;
  const pts = data.map((d, i) => `${xScale(i).toFixed(1)},${yScale(d.value).toFixed(1)}`).join(' ');
  const colour = values[values.length - 1] >= values[0] ? '#34d399' : '#f87171';
  const fillId = `sf-${Math.abs(values[0] | 0)}`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full" style={{ height: H, display: 'block' }}>
      <defs>
        <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor={colour} stopOpacity="0.3" />
          <stop offset="100%" stopColor={colour} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <polygon points={`0,${H} ${pts} ${W},${H}`} fill={`url(#${fillId})`} />
      <polyline points={pts} fill="none" stroke={colour} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

// ── Screener card ─────────────────────────────────────────────────────────────

function ScreenerCardSkeleton() {
  return (
    <Card className="flex flex-col overflow-hidden">
      <CardContent className="flex flex-col gap-3 p-4 flex-1">
        <div>
          <Skeleton className="h-5 w-3/5 mb-1.5" />
          <Skeleton className="h-3 w-2/5" />
        </div>
        <div className="flex gap-4">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-16" />
        </div>
        <div className="flex gap-1.5 mt-auto pt-1 border-t border-border/40">
          <Skeleton className="h-7 flex-1" />
          <Skeleton className="h-7 flex-1" />
          <Skeleton className="h-7 flex-1" />
        </div>
      </CardContent>
    </Card>
  );
}

/** Spec screeners are edited as specs; Python ones in the code editor. */
function editorPath(s) {
  return s.kind === 'code' ? `/builder/${s.id}` : `/spec/${s.id}`;
}

function ScreenerCard({ s, onRename, onDelete, onToggleGlobal, isSuperadmin, navigate }) {
  const hasBacktest = s.win_rate != null;
  const canEdit = s.is_owner;

  return (
    <Card className="flex flex-col overflow-hidden hover:border-border/80 transition-colors group">
      <CardContent className="flex flex-col gap-3 p-4 flex-1 relative">

        {/* Name + file + badges */}
        <div className='flex items-center justify-between w-full'>
          <div className="flex items-center gap-2">
            <div className="font-semibold text-foreground leading-tight">{s.name}</div>
            {s.is_global && (
              <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20">
                <Globe className="h-2.5 w-2.5" /> Global
              </span>
            )}
          </div>
          {/* <div className="text-xs text-muted-foreground mt-0.5 font-mono truncate">{s.file_path}</div> */}
          {canEdit && (
            <div className="flex items-center">
              <Button
                size="sm" variant="ghost"
                className="h-7 px-2 text-xs text-muted-foreground hover:text-primary hover:bg-primary/10"
                title="Rename"
                onClick={(e) => { e.stopPropagation(); onRename(s); }}
              >
                <PenLine className="h-3 w-3" />
              </Button>
              <Button
                size="sm" variant="ghost"
                className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                title="Delete"
                onClick={(e) => { e.stopPropagation(); onDelete(s.id, s.name); }}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="flex gap-4">
          <div className="flex items-center gap-1.5 text-sm">
            <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" />
            <span className={hasBacktest ? (s.win_rate >= 50 ? 'text-win font-medium' : 'text-loss font-medium') : 'text-muted-foreground'}>
              {hasBacktest ? `${s.win_rate.toFixed(0)}% win` : 'No backtest'}
            </span>
          </div>
          <div className="flex items-center gap-1.5 text-sm">
            <Zap className="h-3.5 w-3.5 text-muted-foreground" />
            <span className={s.active_signals > 0 ? 'text-primary font-medium' : 'text-muted-foreground'}>
              {s.active_signals} signal{s.active_signals !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Sparkline */}
        {<div className="absolute left-1/2 h-full w-50 pointer-events-none bg-surface-card/40 px-0 pt-0 overflow-hidden opacity-25">
          <MiniSparkline data={s.equity_mini} />
        </div>}

        {/* Actions */}
        <div className="flex gap-1.5 mt-auto pt-1 border-t border-border/40">
          <Button size="sm" variant="secondary" className="h-7 px-2 text-xs flex-1" onClick={() => navigate(editorPath(s))}>
            {canEdit ? <><Edit2 className="h-3 w-3 mr-1" />Edit</> : <><Eye className="h-3 w-3 mr-1" />View</>}
          </Button>
          <Button size="sm" variant="secondary" className="h-7 px-2 text-xs flex-1" onClick={() => navigate(`/signals/${s.id}`)}>
            <Zap className="h-3 w-3 mr-1" /> Signals
          </Button>
          <Button size="sm" variant="secondary" className="h-7 px-2 text-xs flex-1" onClick={() => navigate(`/backtest/${s.id}`)}>
            <BarChart2 className="h-3 w-3 mr-1" /> Backtest
          </Button>
          {isSuperadmin && (
            <Button
              size="sm" variant="ghost"
              className={`h-7 px-2 text-xs ${s.is_global ? 'text-primary hover:bg-primary/10' : 'text-muted-foreground hover:text-primary hover:bg-primary/10'}`}
              title={s.is_global ? 'Remove from global' : 'Make global'}
              onClick={(e) => { e.stopPropagation(); onToggleGlobal(s.id); }}
            >
              {s.is_global ? <GlobeLock className="h-3 w-3" /> : <Globe className="h-3 w-3" />}
            </Button>
          )}
          
        </div>
      </CardContent>
    </Card>
  );
}

// ── Section heading ───────────────────────────────────────────────────────────

function SectionHeading({ children }) {
  return (
    <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
      {children}
    </h2>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ScreenerList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperadmin = user?.is_superadmin;

  const [screeners, setScreeners]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [newName, setNewName]       = useState('');
  const [newKind, setNewKind]       = useState('spec');
  const [createError, setCreateError] = useState('');
  const [creating, setCreating]     = useState(false);

  const [renameTarget, setRenameTarget] = useState(null);   // screener being renamed
  const [renameName, setRenameName]     = useState('');
  const [renameError, setRenameError]   = useState('');
  const [renaming, setRenaming]         = useState(false);

  const nameInputRef = useRef(null);
  const renameInputRef = useRef(null);
  const { confirm, confirmDialog } = useConfirm();

  const fetchScreeners = useCallback(() => {
    api.get('/screeners')
      .then((r) => setScreeners(r.data))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchScreeners(); }, [fetchScreeners]);

  useEffect(() => {
    if (dialogOpen) setTimeout(() => nameInputRef.current?.focus(), 50);
  }, [dialogOpen]);

  useEffect(() => {
    if (renameTarget) setTimeout(() => renameInputRef.current?.select(), 50);
  }, [renameTarget]);

  const handleCreate = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    setCreateError('');
    try {
      const res = await api.post('/screeners', { name, kind: newKind });
      setScreeners((prev) =>
        [...prev, { ...res.data, equity_mini: [], win_rate: null, active_signals: 0, is_global: false, is_owner: true }]
          .sort((a, b) => a.name.localeCompare(b.name))
      );
      setNewName('');
      setDialogOpen(false);
      navigate(editorPath(res.data));
    } catch (err) {
      setCreateError(err.response?.data?.error || 'Failed to create screener');
    } finally {
      setCreating(false);
    }
  };

  const openRename = (s) => {
    setRenameTarget(s);
    setRenameName(s.name);
    setRenameError('');
  };

  const handleRename = async (e) => {
    e.preventDefault();
    const name = renameName.trim();
    if (!name || !renameTarget) return;
    if (name === renameTarget.name) { setRenameTarget(null); return; }
    setRenaming(true);
    setRenameError('');
    try {
      const res = await api.patch(`/screeners/${renameTarget.id}`, { name });
      setScreeners((prev) =>
        prev.map((s) => (s.id === renameTarget.id ? { ...s, name: res.data.name } : s))
          .sort((a, b) => Number(a.is_global) - Number(b.is_global) || a.name.localeCompare(b.name))
      );
      setRenameTarget(null);
    } catch (err) {
      setRenameError(err.response?.data?.error || 'Failed to rename screener');
    } finally {
      setRenaming(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!(await confirm(`This will permanently delete "${name}".`, {
      title: 'Delete screener?', confirmText: 'Delete',
    }))) return;
    await api.delete(`/screeners/${id}`);
    setScreeners((prev) => prev.filter((s) => s.id !== id));
  };

  const handleToggleGlobal = async (id) => {
    try {
      const res = await api.post(`/admin/screeners/${id}/toggle-global`);
      setScreeners((prev) =>
        prev.map((s) => s.id === id ? { ...s, is_global: res.data.is_global } : s)
      );
    } catch {
      // silent — server will return 403 if not superadmin
    }
  };

  const filtered = screeners.filter((s) =>
    s.name.toLowerCase().includes(search.toLowerCase())
  );

  const myScreeners = filtered.filter((s) => s.is_owner && !s.is_global);
  const globalScreeners = filtered.filter((s) => s.is_global);

  const cardProps = { onRename: openRename, onDelete: handleDelete, onToggleGlobal: handleToggleGlobal, isSuperadmin, navigate };

  return (
    <AppLayout>
      <div className="p-1 md:p-6 max-w-7xl mx-auto">

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <Input
              className="pl-8 h-8 text-sm"
              placeholder="Search screeners…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {loading ? '' : `${myScreeners.length} screener${myScreeners.length !== 1 ? 's' : ''}`}
            </span>
            <Button size="sm" onClick={() => { setDialogOpen(true); setCreateError(''); setNewName(''); }}>
              <Plus className="h-3.5 w-3.5 mr-1" /> New
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => <ScreenerCardSkeleton key={i} />)}
          </div>
        ) : (
          <>
            {/* My screeners */}
            {myScreeners.length === 0 && !search ? (
              <div className="text-center py-20 text-muted-foreground text-sm">
                No screeners yet. <button className="text-primary underline-offset-2 hover:underline" onClick={() => setDialogOpen(true)}>Create one</button>.
              </div>
            ) : myScreeners.length === 0 && search ? null : (
              <>
                {globalScreeners.length > 0 && <SectionHeading>My Screeners</SectionHeading>}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4">
                  {myScreeners.map((s) => (
                    <ScreenerCard key={s.id} s={s} {...cardProps} />
                  ))}
                </div>
              </>
            )}

            {/* Global screeners */}
            {globalScreeners.length > 0 && (
              <div className={myScreeners.length > 0 ? 'mt-8' : ''}>
                <SectionHeading>
                  <Globe className="inline h-3 w-3 mr-1 mb-0.5" />
                  Global Screeners
                </SectionHeading>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4">
                  {globalScreeners.map((s) => (
                    <ScreenerCard key={s.id} s={s} {...cardProps} />
                  ))}
                </div>
              </div>
            )}

            {search && filtered.length === 0 && (
              <div className="text-center py-20 text-muted-foreground text-sm">No screeners match your search.</div>
            )}
          </>
        )}

      </div>

      {/* Create dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>New Screener</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="flex flex-col gap-3 mt-1">
            <Input
              ref={nameInputRef}
              placeholder="screener_name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              pattern="[a-zA-Z0-9_\-]+"
              title="Alphanumeric, underscore or dash"
              required
            />
            {user?.can_author_code && (
              <div className="flex flex-col gap-1.5">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio" name="kind" value="spec" className="mt-1"
                    checked={newKind === 'spec'}
                    onChange={() => setNewKind('spec')}
                  />
                  <span className="text-xs">
                    <span className="text-foreground">Build it</span>
                    <span className="text-muted-foreground"> — pick attributes and conditions in a form</span>
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio" name="kind" value="code" className="mt-1"
                    checked={newKind === 'code'}
                    onChange={() => setNewKind('code')}
                  />
                  <span className="text-xs">
                    <span className="text-foreground">Write Python</span>
                    <span className="text-muted-foreground"> — advanced; full access to the attribute API</span>
                  </span>
                </label>
              </div>
            )}
            {createError && <p className="text-xs text-destructive">{createError}</p>}
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="ghost" size="sm" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" size="sm" disabled={creating}>{creating ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />Creating…</> : 'Create'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Rename dialog */}
      <Dialog open={!!renameTarget} onOpenChange={(open) => { if (!open) setRenameTarget(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Rename Screener</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRename} className="flex flex-col gap-3 mt-1">
            <Input
              ref={renameInputRef}
              placeholder="screener_name"
              value={renameName}
              onChange={(e) => setRenameName(e.target.value)}
              pattern="[a-zA-Z0-9_\-]+"
              title="Alphanumeric, underscore or dash"
              required
            />
            {renameError && <p className="text-xs text-destructive">{renameError}</p>}
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="ghost" size="sm" onClick={() => setRenameTarget(null)}>Cancel</Button>
              <Button type="submit" size="sm" disabled={renaming}>{renaming ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />Saving…</> : 'Save'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {confirmDialog}
    </AppLayout>
  );
}
